import { Button, Text } from "@radix-ui/themes"
import { FileText, Upload } from "lucide-react"
import { useRef, useState } from "react"
import clsx from "clsx"

import InputError from "./input-error"

interface FileUploadProps {
  file: File | null
  onFileSelect: (file: File | null) => void
  accept?: string
  disabled?: boolean
  label?: string
  description?: string
  error?: string
  required?: boolean
}

export default function FileUpload({
  file,
  onFileSelect,
  accept = ".csv",
  disabled = false,
  label,
  description,
  error,
  required = false,
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const dragCounter = useRef(0)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    const mime = getAcceptedMimeType(accept)
    if (selectedFile && (accept === "*" || mime === "*/*" || selectedFile.type === mime)) {
      onFileSelect(selectedFile)
    }
  }

  const triggerFileDialog = () => {
    fileInputRef.current?.click()
  }

  const getAcceptedMimeType = (acceptString: string) => {
    if (acceptString === ".csv") return "text/csv"
    return "*/*"
  }

  const getFileExtension = () => {
    return accept.replace(".", "").toUpperCase()
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragIn = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (!disabled && e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true)
    }
  }

  const handleDragOut = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragActive(false)

    if (disabled) return

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      const mime = getAcceptedMimeType(accept)
      if (accept === "*" || mime === "*/*" || droppedFile.type === mime) {
        onFileSelect(droppedFile)
      }
    }
  }

  return (
    <div>
      {label && (
        <Text as="div" size="3" weight="bold" mb="2">
          {label} {required && <span className="text-red-500">*</span>}
        </Text>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      <div
        className={clsx(
          "rounded border-2 border-dashed p-8 text-center transition-colors",
          isDragActive ? "border-blue-400 bg-blue-50" : "border-stone-200",
        )}
        onDragOver={handleDrag}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDrop={handleDrop}
        onClick={triggerFileDialog}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            triggerFileDialog()
          }
        }}
      >
        {file ? (
          <div className="flex flex-col items-center gap-3">
            <FileText className="h-12 w-12 text-green-500" />
            <div>
              <Text size="3" weight="medium">
                {file.name}
              </Text>
              <Text size="2" color="gray" as="div">
                {(file.size / 1024).toFixed(1)} KB
              </Text>
            </div>
            <Button type="button" variant="soft" onClick={triggerFileDialog} disabled={disabled}>
              Choose different file
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="h-12 w-12 text-gray-400" />
            <div>
              <Text size="3" weight="medium" as="div">
                {isDragActive ? "Drop file here" : `Click to select ${getFileExtension()} file`}
              </Text>
              {description && (
                <Text size="2" color="gray" as="div">
                  {description}
                </Text>
              )}
            </div>
            <Button type="button" onClick={triggerFileDialog} disabled={disabled || isDragActive}>
              Select file
            </Button>
          </div>
        )}
      </div>

      <InputError message={error} />
    </div>
  )
}
